import { useEffect, useState, useCallback } from "react";
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { useLocation } from "wouter";
import { useEmployerTour } from "./EmployerTourManager";
import { employerTourSteps } from "./employerTourSteps";
import { tourStyles, TOUR_CONFIG } from "./tourConfig";
import { TourStepExtended } from "./tourSteps";

export function EmployerTour() {
  const { isTourRunning, stopTour, completeTour, skipTour } = useEmployerTour();
  const [location, setLocation] = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);

  // Handle navigation requirements for current step
  useEffect(() => {
    if (!isTourRunning || isNavigating) return;
    const currentStep = employerTourSteps[stepIndex] as TourStepExtended;
    if (currentStep?.requiresNavigation) {
      const { path } = currentStep.requiresNavigation;
      if (location !== path) {
        setIsNavigating(true);
        setLocation(path);
        setTimeout(() => setIsNavigating(false), 500);
      }
    }
  }, [stepIndex, isTourRunning, location, setLocation, isNavigating]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (action === ACTIONS.NEXT && nextIndex < employerTourSteps.length) {
        setStepIndex(nextIndex);
      } else if (action === ACTIONS.NEXT && nextIndex >= employerTourSteps.length) {
        completeTour();
        setStepIndex(0);
        return;
      } else if (action === ACTIONS.PREV && nextIndex >= 0) {
        setStepIndex(nextIndex);
      }
    }

    if (status === STATUS.FINISHED) {
      completeTour();
      setStepIndex(0);
    } else if (status === STATUS.SKIPPED) {
      skipTour();
      setStepIndex(0);
    } else if (action === ACTIONS.CLOSE) {
      stopTour();
      setStepIndex(0);
    }
  }, [completeTour, skipTour, stopTour]);

  useEffect(() => {
    if (isTourRunning) setStepIndex(0);
  }, [isTourRunning]);

  if (!isTourRunning) return null;

  return (
    <Joyride
      steps={employerTourSteps}
      stepIndex={stepIndex}
      run={isTourRunning && !isNavigating}
      callback={handleJoyrideCallback}
      continuous={TOUR_CONFIG.continuous}
      showProgress={TOUR_CONFIG.showProgress}
      showSkipButton={TOUR_CONFIG.showSkipButton}
      disableCloseOnEsc={TOUR_CONFIG.disableCloseOnEsc}
      disableOverlayClose={TOUR_CONFIG.disableOverlayClose}
      spotlightClicks={TOUR_CONFIG.spotlightClicks}
      hideBackButton={TOUR_CONFIG.hideBackButton}
      locale={TOUR_CONFIG.locale}
      styles={tourStyles}
      scrollToFirstStep
      scrollOffset={100}
    />
  );
}
